import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';
import Toast from '../components/Toast';
import { API_BASE_URL } from '../config/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to make authenticated requests
const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function SessionDetailPage() {
  const { date } = useParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    document.title = `Visitor Sessions - ${date} - Design Store Admin`;
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSessions runs when date changes
  }, [date]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/visitor-sessions/${date}`);
      
      if (response.data.success) {
        setSessions(response.data.data);
      } else {
        setError('Failed to fetch visitor sessions');
      }
    } catch (err) {
      console.error('Error fetching visitor sessions:', err);
      setError(err.response?.data?.error || 'Failed to fetch visitor sessions');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const toggleSession = (sessionId) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <button
                onClick={() => navigate('/admin/visitor-activity')}
                className="text-blue-600 hover:text-blue-800 mb-2 flex items-center gap-1"
              >
                ← Back to Calendar
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                Visitor Sessions - {formatDate(date)}
              </h1>
              <p className="text-gray-600 mt-1">{sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}</p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading sessions...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No visitor sessions found for this date.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <div
                  key={session.sessionId}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <h3 className="font-semibold text-gray-900">
                          Session: {session.sessionId.substring(0, 20)}...
                        </h3>
                        <span className="text-sm text-gray-500">
                          {session.activityCount} {session.activityCount === 1 ? 'page visit' : 'page visits'}
                        </span>
                        <span className="text-sm text-gray-500">
                          Duration: {formatDuration(session.duration)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Started:</span> {formatDateTime(session.startTime)}
                        </div>
                        <div>
                          <span className="font-medium">Last Activity:</span> {formatDateTime(session.lastActivity)}
                        </div>
                        <div>
                          <span className="font-medium">IP Address:</span> {session.ipAddress}
                        </div>
                        <div>
                          <span className="font-medium">User Agent:</span>{' '}
                          <span className="text-xs">{session.userAgent.substring(0, 50)}...</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleSession(session.sessionId)}
                      className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {expandedSession === session.sessionId ? 'Hide' : 'Show'} Activities
                    </button>
                  </div>

                  {expandedSession === session.sessionId && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-2">Page Visits:</h4>
                      <div className="space-y-2">
                        {session.activities && session.activities.length > 0 ? (
                          session.activities.map((activity, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 rounded p-3 text-sm"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-gray-900">{activity.path}</span>
                                    {activity.eventType && activity.eventType !== 'page_view' && (
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                        {activity.eventType.replace('_', ' ')}
                                      </span>
                                    )}
                                  </div>
                                  {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                                    <div className="text-gray-600 text-xs mt-1 space-y-0.5">
                                      {activity.eventType === 'add_to_cart' && (
                                        <>
                                          <div><strong>Product:</strong> {activity.metadata.designTitle} (ID: {activity.metadata.designId})</div>
                                          <div><strong>Format:</strong> {activity.metadata.formatName} ({activity.metadata.productType})</div>
                                          <div><strong>Price:</strong> ${activity.metadata.price?.toFixed(2)} × {activity.metadata.quantity}</div>
                                        </>
                                      )}
                                      {activity.eventType === 'product_click' && (
                                        <div><strong>Product:</strong> {activity.metadata.designTitle} (ID: {activity.metadata.designId})</div>
                                      )}
                                      {activity.eventType === 'tag_click' && (
                                        <div><strong>Tag:</strong> {activity.metadata.tag}</div>
                                      )}
                                      {activity.eventType === 'category_click' && (
                                        <div><strong>Category:</strong> {activity.metadata.category}</div>
                                      )}
                                      {activity.eventType === 'format_click' && (
                                        <div><strong>Format:</strong> {activity.metadata.formatName} ({activity.metadata.productType})</div>
                                      )}
                                      {activity.eventType === 'cart_view' && (
                                        <div><strong>Event:</strong> Viewed shopping cart</div>
                                      )}
                                    </div>
                                  )}
                                  {activity.referrer && (
                                    <div className="text-gray-500 text-xs mt-1">
                                      From: {activity.referrer}
                                    </div>
                                  )}
                                </div>
                                <div className="text-gray-500 text-xs ml-2">
                                  {formatDateTime(activity.timestamp)}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-sm">No activities recorded</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default SessionDetailPage;

